import React, { FC } from 'react';
import { CaretDown } from '@phosphor-icons/react';
import { FormStepIntensity } from '../workoutBuilderTypes';
import { REF_OPTIONS, ABS_UNIT_OPTIONS } from './conditioningSteps.constants';
import { useConditioningResolve } from './useConditioningResolve';
import { IStepResolved } from '../../../../endpoints/profiling';
import styles from './ConditioningSteps.module.scss';

interface StepIntensityFieldProps {
  value: FormStepIntensity;
  onChange: (i: FormStepIntensity) => void;
  /** Atleta di anteprima: se presente, mostra la conversione %→assoluto (BUG E). */
  previewAthleteId?: string;
}

/** Messaggio di warning quando la valuta non è risolvibile per l'atleta. */
function unresolvedMessage(r: IStepResolved | null): string | null {
  if (!r || !r.unresolved) return null;
  switch (r.reason) {
    case 'no_profile_vam':
      return "Nessun test VAM per l'atleta";
    case 'no_profile_vift':
      return "Nessun test VIFT per l'atleta";
    case 'no_ftp_protocol':
      return 'FTP non ancora disponibile';
    case 'vam_implausible':
    case 'vift_implausible':
      return 'Valore di riferimento anomalo';
    default:
      return null; // 'incomplete' → niente rumore
  }
}

const StepIntensityField: FC<StepIntensityFieldProps> = ({ value, onChange, previewAthleteId }) => {
  const setMode = (mode: 'profile' | 'absolute') => {
    onChange({ ...value, mode });
  };

  // Preview conversione %→assoluto (solo in modo profilo, con atleta selezionato)
  const { resolved } = useConditioningResolve(
    value.mode === 'profile' ? previewAthleteId : undefined,
    value.ref,
    value.pct,
  );
  const warning = unresolvedMessage(resolved);
  const absPreview =
    resolved && !resolved.unresolved && resolved.absValue != null
      ? resolved.paceFormatted
        ? `≈ ${resolved.paceFormatted}`
        : `≈ ${resolved.absValue}${resolved.absUnit ? ` ${resolved.absUnit}` : ''}`
      : null;
  // Hint deterministico (indipendente dall'atleta): FTP non è ancora supportata.
  const ftpHint = value.mode === 'profile' && value.ref === 'ftp' && !previewAthleteId;

  return (
    <div className={styles.fieldGroup}>
      <span className={styles.fieldLabel}>Intensità</span>

      <div className={styles.toggleRow}>
        <button
          type="button"
          className={value.mode === 'profile' ? styles.toggleBtnActive : styles.toggleBtn}
          onClick={() => setMode('profile')}
        >
          Profilo %
        </button>
        <button
          type="button"
          className={value.mode === 'absolute' ? styles.toggleBtnActive : styles.toggleBtn}
          onClick={() => setMode('absolute')}
        >
          Assoluto
        </button>
      </div>

      {value.mode === 'profile' ? (
        <>
          <div className={styles.inlineRow}>
            <div className={styles.selectWrapper}>
              <select
                className={styles.compactSelect}
                value={value.ref}
                onChange={(e) =>
                  onChange({ ...value, ref: e.target.value as FormStepIntensity['ref'] })
                }
              >
                {REF_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <CaretDown size={11} className={styles.selectIcon} />
            </div>
            <div className={styles.inputWithSuffix}>
              <input
                type="number"
                className={styles.compactInput}
                placeholder="80"
                value={value.pct}
                min={0}
                max={200}
                onChange={(e) => onChange({ ...value, pct: e.target.value })}
              />
              <span className={styles.inputSuffix}>%</span>
            </div>
          </div>
          {absPreview && <span className={styles.intensityPreview}>{absPreview}</span>}
          {warning && <span className={styles.intensityWarning}>{warning}</span>}
          {ftpHint && (
            <span className={styles.intensityWarning}>FTP non disponibile — usa Assoluto (W)</span>
          )}
        </>
      ) : (
        <div className={styles.inlineRow}>
          <input
            type="number"
            className={styles.compactInput}
            placeholder="15"
            value={value.absValue}
            min={0}
            onChange={(e) => onChange({ ...value, absValue: e.target.value })}
          />
          <div className={styles.selectWrapper}>
            <select
              className={styles.compactSelect}
              value={value.absUnit}
              onChange={(e) => onChange({ ...value, absUnit: e.target.value })}
            >
              {ABS_UNIT_OPTIONS.map((unit) => (
                <option key={unit} value={unit}>
                  {unit}
                </option>
              ))}
            </select>
            <CaretDown size={11} className={styles.selectIcon} />
          </div>
        </div>
      )}
    </div>
  );
};

export default StepIntensityField;
